import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const TareasKanban = () => {
  const { user, API } = useContext(AuthContext);
  const [tareas, setTareas] = useState([]);
  const [clases, setClases] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTarea, setSelectedTarea] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [comentarioText, setComentarioText] = useState('');
  const [comentarioFile, setComentarioFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [editingAsignados, setEditingAsignados] = useState(false);

  const [newTarea, setNewTarea] = useState({
    titulo: '',
    descripcion: '',
    clase_id: '',
    usuarios_asignados: [],
    fecha_limite: '',
    prioridad: 'media',
    tipo_asignacion: 'profesor_a_estudiante',
    crear_calificacion: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tareasRes, clasesRes, usuariosRes] = await Promise.all([
        axios.get(`${API}/tareas`),
        axios.get(`${API}/clases`),
        axios.get(`${API}/users`)
      ]);
      setTareas(tareasRes.data);
      setClases(clasesRes.data);
      setUsuarios(usuariosRes.data);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTarea = async (e) => {
    e.preventDefault();
    if (!newTarea.titulo || !newTarea.descripcion || !newTarea.clase_id || !newTarea.fecha_limite) {
      toast.error('Por favor completa todos los campos obligatorios');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/tareas`, {
        ...newTarea,
        fecha_limite: newTarea.fecha_limite + 'T12:00:00'
      });
      
      setTareas([response.data, ...tareas]);
      setNewTarea({ 
        titulo: '', 
        descripcion: '', 
        clase_id: '', 
        usuarios_asignados: [], 
        fecha_limite: '',
        prioridad: 'media',
        tipo_asignacion: user.rol === 'admin' ? 'admin_a_profesor' : 'profesor_a_estudiante'
      });
      setIsCreateDialogOpen(false);
      toast.success('✅ Tarea creada y notificaciones push enviadas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear tarea');
    } finally {
      setSending(false);
    }
  };

  const handleCambiarEstado = async (tareaId, nuevoEstado) => {
    try {
      const response = await axios.put(`${API}/tareas/${tareaId}/estado`, {
        estado: nuevoEstado
      });
      
      // Functional update para evitar closures stale
      setTareas(prev => prev.map(t => t.id === tareaId ? response.data : t));
      
      // Si estamos viendo detalles, actualizar
      if (selectedTarea?.id === tareaId) {
        setSelectedTarea(response.data);
      }

      if (nuevoEstado === 'Completa') {
        toast.success('✅ Tarea marcada como completa');
      } else {
        toast.success(`Tarea movida a ${nuevoEstado}`);
      }

      // Refetch en background para garantizar consistencia
      loadData();
    } catch (error) {
      console.error('Error cambiar estado:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleAgregarComentario = async (e) => {
    e.preventDefault();
    if (!comentarioText.trim()) {
      toast.error('Escribe un comentario');
      return;
    }

    try {
      if (comentarioFile) {
        // Comentario con archivo adjunto
        const formData = new FormData();
        formData.append('texto', comentarioText);
        formData.append('file', comentarioFile);

        const response = await axios.post(
          `${API}/tareas/${selectedTarea.id}/comentarios/adjunto`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          }
        );
        
        setSelectedTarea(response.data);
        setTareas(tareas.map(t => t.id === selectedTarea.id ? response.data : t));
        setComentarioText('');
        setComentarioFile(null);
        // Resetear el input de archivo
        const fileInput = document.getElementById('comentario-file-input');
        if (fileInput) fileInput.value = '';
        toast.success('💬📎 Comentario con archivo agregado');
      } else {
        // Comentario sin archivo
        const response = await axios.post(`${API}/tareas/${selectedTarea.id}/comentarios`, {
          texto: comentarioText
        });
        
        setSelectedTarea(response.data);
        setTareas(tareas.map(t => t.id === selectedTarea.id ? response.data : t));
        setComentarioText('');
        setComentarioFile(null);
        // Resetear el input de archivo
        const fileInput = document.getElementById('comentario-file-input');
        if (fileInput) fileInput.value = '';
        toast.success('💬 Comentario agregado');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agregar comentario');
    }
  };

  const handleEliminarTarea = async (tareaId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta tarea? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      await axios.delete(`${API}/tareas/${tareaId}`);
      
      // Remover de la lista
      setTareas(tareas.filter(t => t.id !== tareaId));
      setIsDetailDialogOpen(false);
      toast.success('🗑️ Tarea eliminada exitosamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar tarea');
    }
  };


  const handleUpdateAsignados = async (tareaId, nuevosAsignados) => {
    try {
      const response = await axios.put(`${API}/tareas/${tareaId}`, {
        usuarios_asignados: nuevosAsignados
      });
      setTareas(tareas.map(t => t.id === tareaId ? response.data : t));
      setSelectedTarea(response.data);
      toast.success('Asignacion actualizada');
      setEditingAsignados(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };


  const handleCambiarEstadoAdmin = async (tareaId, nuevoEstado) => {
    try {
      const response = await axios.put(`${API}/tareas/${tareaId}/cambiar-estado`, {
        estado: nuevoEstado
      });
      
      // Functional update para evitar closures stale
      setTareas(prev => prev.map(t => t.id === tareaId ? response.data : t));
      setSelectedTarea(response.data);
      
      toast.success(`✅ Estado cambiado a: ${nuevoEstado}`);

      // Refetch en background para garantizar consistencia
      loadData();
    } catch (error) {
      console.error('Error cambiar estado admin:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const getDiasRestantes = (fechaLimite) => {
    const hoy = new Date();
    const limite = new Date(fechaLimite);
    const diff = Math.ceil((limite - hoy) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getPrioridadColor = (prioridad) => {
    const colors = {
      alta: 'bg-red-100 text-red-800 border-red-300',
      media: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      baja: 'bg-green-100 text-green-800 border-green-300'
    };
    return colors[prioridad] || colors.media;
  };

  const getUrgenciaColor = (diasRestantes) => {
    if (diasRestantes < 0) return 'text-red-600 font-bold';
    if (diasRestantes <= 1) return 'text-red-500 font-bold';
    if (diasRestantes <= 3) return 'text-orange-500 font-semibold';
    return 'text-gray-600';
  };

  const renderTareaCard = (tarea) => {
    const diasRestantes = getDiasRestantes(tarea.fecha_limite);
    const clase = clases.find(c => c.id === tarea.clase_id);
    const esAsignado = tarea.usuarios_asignados?.includes(user.id);
    const esCreador = tarea.profesor_id === user.id || user.rol === 'admin';

    return (
      <Card 
        key={tarea.id} 
        data-testid={`card-tarea-${tarea.id}`}
        data-estado={tarea.estado}
        className="mb-3 cursor-pointer hover:shadow-md transition-shadow border-l-4"
        style={{
          borderLeftColor: tarea.prioridad === 'alta' ? '#ef4444' : 
                          tarea.prioridad === 'media' ? '#f59e0b' : '#10b981'
        }}
        onClick={() => {
          setSelectedTarea(tarea);
          setIsDetailDialogOpen(true);
        }}
      >
        <CardContent className="p-4">
          <div className="space-y-2">
            <div className="flex items-start justify-between">
              <h4 className="font-semibold text-sm line-clamp-2">{tarea.titulo}</h4>
              <Badge className={`ml-2 text-xs ${getPrioridadColor(tarea.prioridad)}`}>
                {tarea.prioridad}
              </Badge>
            </div>

            <p className="text-xs text-gray-600 line-clamp-2">{tarea.descripcion}</p>

            {clase && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <i className="fas fa-book" />
                <span className="truncate">{clase.titulo}</span>
              </div>
            )}

            {/* Asignados */}
            {tarea.usuarios_asignados?.length > 0 && (
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <i className="fas fa-user" />
                <span className="truncate">
                  {tarea.usuarios_asignados.map(uid => {
                    const u = usuarios.find(u => u.id === uid);
                    return u ? `${u.nombre} ${u.apellido || ''}`.trim() : '';
                  }).filter(Boolean).join(', ') || `${tarea.usuarios_asignados.length} asignado(s)`}
                </span>
              </div>
            )}

            {/* Fecha de vencimiento */}
            {tarea.fecha_limite && (
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <i className="fas fa-calendar-alt" />
                <span>{new Date(tarea.fecha_limite).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
              </div>
            )}

            <div className="flex items-center justify-between text-xs">
              <div className={`flex items-center gap-1 ${getUrgenciaColor(diasRestantes)}`}>
                <i className="fas fa-clock" />
                <span>
                  {diasRestantes < 0 ? `Vencida hace ${Math.abs(diasRestantes)}d` :
                   diasRestantes === 0 ? 'Vence hoy' :
                   diasRestantes === 1 ? 'Vence mañana' :
                   `${diasRestantes} días`}
                </span>
              </div>

              {(tarea.comentarios?.length > 0 || tarea.adjuntos?.length > 0) && (
                <div className="flex items-center gap-2 text-gray-500">
                  {tarea.comentarios?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <i className="fas fa-comment" />
                      {tarea.comentarios.length}
                    </span>
                  )}
                  {tarea.adjuntos?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <i className="fas fa-paperclip" />
                      {tarea.adjuntos.length}
                    </span>
                  )}
                </div>
              )}
            </div>

            {esAsignado && tarea.estado !== 'Completa' && (
              <div className="flex gap-2 mt-2 pt-2 border-t">
                {tarea.estado === 'Enviada' && (
                  <Button
                    size="sm"
                    className="text-xs flex-1 bg-yellow-500 hover:bg-yellow-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCambiarEstado(tarea.id, 'Ejecutándose');
                    }}
                  >
                    <i className="fas fa-play mr-1" />
                    Iniciar
                  </Button>
                )}
                {tarea.estado === 'Ejecutándose' && (
                  <Button
                    size="sm"
                    className="text-xs flex-1 bg-green-500 hover:bg-green-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCambiarEstado(tarea.id, 'Completa');
                    }}
                  >
                    <i className="fas fa-check mr-1" />
                    Completar
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const sortByFechaVencimiento = (lista) => [...lista].sort((a, b) => {
    const fechaA = a.fecha_limite || a.fecha_vencimiento;
    const fechaB = b.fecha_limite || b.fecha_vencimiento;
    if (!fechaA) return 1;
    if (!fechaB) return -1;
    return new Date(fechaA) - new Date(fechaB);
  });

  const tareasPorEstado = {
    'Enviada': sortByFechaVencimiento(tareas.filter(t => t.estado === 'Enviada')),
    'Ejecutándose': sortByFechaVencimiento(tareas.filter(t => t.estado === 'Ejecutándose')),
    'Completa': sortByFechaVencimiento(tareas.filter(t => t.estado === 'Completa'))
  };

  // Filtrar usuarios según tipo de asignación
  const getUsuariosDisponibles = () => {
    if (user.rol === 'admin' && newTarea.tipo_asignacion === 'admin_a_profesor') {
      return usuarios.filter(u => u.rol === 'profesor' && u.activo !== false);
    }
    if (user.rol === 'admin' && newTarea.tipo_asignacion === 'admin_a_pastor') {
      return usuarios.filter(u => u.rol === 'pastor' && u.activo !== false);
    }
    if (user.rol === 'admin' && newTarea.tipo_asignacion === 'admin_a_admin') {
      return usuarios.filter(u => u.rol === 'admin' && u.activo !== false);
    }
    return usuarios.filter(u => (u.rol === 'estudiante' || u.rol === 'diploma' || u.rol === 'pastor' || u.rol === 'graduado') && u.activo !== false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-500 mb-4" />
          <p className="text-gray-600">Cargando tareas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard'}
            className="text-gray-600 hover:text-gray-800"
            data-testid="back-button"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Volver
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-800">📋 Sistema de Tareas</h1>
            <p className="text-gray-600 mt-1">
              {user.rol === 'admin' ? 'Gestiona tareas para profesores y estudiantes' :
               user.rol === 'profesor' ? 'Gestiona tareas para tus estudiantes' :
               'Visualiza y completa tus tareas asignadas'}
            </p>
          </div>
        </div>

        {(user.rol === 'admin' || user.rol === 'profesor') && (
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg">
                <i className="fas fa-plus mr-2" />
                Nueva Tarea
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[850px] max-h-[90vh] p-0 overflow-hidden">
              <div className="flex flex-col h-full max-h-[90vh]">
                {/* Header con gradiente */}
                <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-5 border-b">
                  <DialogTitle className="text-white text-2xl font-bold flex items-center gap-3">
                    <i className="fas fa-plus-circle text-3xl" />
                    Crear Nueva Tarea
                  </DialogTitle>
                  <p className="text-blue-100 text-sm mt-2">
                    {user.rol === 'admin' 
                      ? 'Asigna tareas a profesores o estudiantes' 
                      : 'Asigna tareas a tus estudiantes'}
                  </p>
                </div>

                {/* Contenido scrolleable */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-4">
                  <form id="crear-tarea-form" onSubmit={handleCreateTarea} className="space-y-5 w-full">
                    {user.rol === 'admin' && (
                      <Card className="border-2 border-indigo-200 bg-indigo-50">
                        <CardContent className="p-4">
                          <Label className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i className="fas fa-users text-indigo-600" />
                            Tipo de Asignación
                          </Label>
                          <Select 
                            value={newTarea.tipo_asignacion}
                            onValueChange={(value) => setNewTarea({...newTarea, tipo_asignacion: value, usuarios_asignados: []})}
                          >
                            <SelectTrigger className="border-2 border-indigo-300 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="profesor_a_estudiante">
                                <span className="flex items-center gap-2">
                                  <i className="fas fa-graduation-cap text-blue-600" />
                                  Para Estudiantes
                                </span>
                              </SelectItem>
                              <SelectItem value="admin_a_profesor">
                                <span className="flex items-center gap-2">
                                  <i className="fas fa-chalkboard-teacher text-green-600" />
                                  Para Profesores
                                </span>
                              </SelectItem>
                              <SelectItem value="admin_a_pastor">
                                <span className="flex items-center gap-2">
                                  <i className="fas fa-cross text-purple-600" />
                                  Para Pastores
                                </span>
                              </SelectItem>
                              <SelectItem value="admin_a_admin">
                                <span className="flex items-center gap-2">
                                  <i className="fas fa-user-shield text-red-600" />
                                  Para Administradores
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </CardContent>
                      </Card>
                    )}

                    <Card className="border-2 border-gray-200">
                      <CardContent className="p-4 space-y-4">
                        <div>
                          <Label className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i className="fas fa-heading text-purple-600" />
                            Título de la Tarea *
                          </Label>
                          <Input
                            value={newTarea.titulo}
                            onChange={(e) => setNewTarea({...newTarea, titulo: e.target.value})}
                            placeholder="Ej: Ensayo sobre Teología Paulina"
                            className="border-2 focus:border-purple-500"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            💡 Usa un título claro y descriptivo
                          </p>
                        </div>

                        <div>
                          <Label className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i className="fas fa-align-left text-purple-600" />
                            Descripción Detallada *
                          </Label>
                          <Textarea
                            value={newTarea.descripcion}
                            onChange={(e) => setNewTarea({...newTarea, descripcion: e.target.value})}
                            placeholder="Describe claramente qué deben hacer, cómo, formato esperado, criterios de evaluación..."
                            rows={5}
                            className="border-2 focus:border-purple-500 resize-none"
                            required
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            💡 Incluye instrucciones claras, formato y criterios de evaluación
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Card className="border-2 border-purple-200 bg-purple-50">
                        <CardContent className="p-4">
                          <Label className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i className="fas fa-book text-purple-600" />
                            Clase *
                          </Label>
                          <Select 
                            value={newTarea.clase_id}
                            onValueChange={(value) => setNewTarea({...newTarea, clase_id: value})}
                          >
                            <SelectTrigger className="border-2 border-purple-300 bg-white">
                              <SelectValue placeholder="Selecciona una clase" />
                            </SelectTrigger>
                            <SelectContent>
                              {clases.map(clase => (
                                <SelectItem key={clase.id} value={clase.id}>
                                  {clase.titulo}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </CardContent>
                      </Card>

                      <Card className={`border-2 ${
                        newTarea.prioridad === 'alta' ? 'border-red-200 bg-red-50' :
                        newTarea.prioridad === 'media' ? 'border-yellow-200 bg-yellow-50' :
                        'border-green-200 bg-green-50'
                      }`}>
                        <CardContent className="p-4">
                          <Label className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
                            <i className="fas fa-star text-orange-600" />
                            Prioridad
                          </Label>
                          <Select 
                            value={newTarea.prioridad}
                            onValueChange={(value) => setNewTarea({...newTarea, prioridad: value})}
                          >
                            <SelectTrigger className={`border-2 bg-white ${
                              newTarea.prioridad === 'alta' ? 'border-red-300' :
                              newTarea.prioridad === 'media' ? 'border-yellow-300' :
                              'border-green-300'
                            }`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="baja">
                                <span className="flex items-center gap-2">
                                  🟢 Baja - Opcional o con mucho tiempo
                                </span>
                              </SelectItem>
                              <SelectItem value="media">
                                <span className="flex items-center gap-2">
                                  🟡 Media - Tarea normal del curso
                                </span>
                              </SelectItem>
                              <SelectItem value="alta">
                                <span className="flex items-center gap-2">
                                  🔴 Alta - Urgente o muy importante
                                </span>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </CardContent>
                      </Card>
                    </div>

                    <Card className="border-2 border-blue-200 bg-blue-50">
                      <CardContent className="p-4">
                        <Label className="text-base font-semibold text-gray-800 mb-2 flex items-center gap-2">
                          <i className="fas fa-calendar-alt text-blue-600" />
                          Fecha Límite *
                        </Label>
                        <Input
                          type="date"
                          value={newTarea.fecha_limite}
                          onChange={(e) => setNewTarea({...newTarea, fecha_limite: e.target.value})}
                          className="border-2 border-blue-300 bg-white"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Recomendacion: Da minimo 5-7 dias para tareas complejas
                        </p>
                      </CardContent>
                    </Card>

                    {/* Checkbox de Calificación Automática */}
                    <Card className="border-2 border-purple-200 bg-purple-50">
                      <CardContent className="p-4">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newTarea.crear_calificacion}
                            onChange={(e) => setNewTarea({...newTarea, crear_calificacion: e.target.checked})}
                            className="w-5 h-5 mt-1 rounded text-purple-600 focus:ring-purple-500"
                          />
                          <div className="flex-1">
                            <span className="text-base font-semibold text-gray-800 flex items-center gap-2">
                              <i className="fas fa-chart-line text-purple-600" />
                              Crear Calificación Automática (Sistema Gamificado)
                            </span>
                            <p className="text-xs text-gray-700 mt-2">
                              <strong>🎮 Sistema de Puntos:</strong>
                            </p>
                            <ul className="text-xs text-gray-600 mt-1 space-y-1">
                              <li>• 📥 <strong>Enviada:</strong> 0 puntos</li>
                              <li>• ⚡ <strong>Ejecutándose:</strong> +30 puntos (por iniciar)</li>
                              <li>• ✅ <strong>Completa:</strong> +30 puntos más (60 total)</li>
                              <li>• ⭐ <strong>Bonus Profesor:</strong> +40 puntos (calidad del trabajo)</li>
                              <li className="pt-1 font-semibold text-purple-700">📊 Total máximo: 100 puntos</li>
                            </ul>
                          </div>
                        </label>
                      </CardContent>
                    </Card>

                    <Card className="border-2 border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <Label className="text-base font-semibold text-gray-800 mb-3 flex items-center justify-between">
                          <span className="flex items-center gap-2">
                            <i className="fas fa-user-check text-green-600" />
                            Asignar a {
                              newTarea.tipo_asignacion === 'admin_a_profesor' ? 'Profesores' : 
                              newTarea.tipo_asignacion === 'admin_a_pastor' ? 'Pastores' :
                              newTarea.tipo_asignacion === 'admin_a_admin' ? 'Administradores' :
                              'Estudiantes'
                            }
                          </span>
                          <Badge className="bg-green-600 text-white">
                            {newTarea.usuarios_asignados.length} seleccionado(s)
                          </Badge>
                        </Label>
                        <div className="border-2 border-green-300 rounded-lg p-3 max-h-48 overflow-y-auto space-y-2 bg-white">
                          {getUsuariosDisponibles().length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <i className="fas fa-users text-4xl mb-2" />
                              <p className="text-sm">No hay usuarios disponibles</p>
                            </div>
                          ) : (
                            getUsuariosDisponibles().map(usuario => (
                              <label 
                                key={usuario.id} 
                                className="flex items-center gap-3 cursor-pointer hover:bg-green-50 p-3 rounded-lg border border-transparent hover:border-green-300 transition-all"
                              >
                                <input
                                  type="checkbox"
                                  checked={newTarea.usuarios_asignados.includes(usuario.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewTarea({
                                        ...newTarea,
                                        usuarios_asignados: [...newTarea.usuarios_asignados, usuario.id]
                                      });
                                    } else {
                                      setNewTarea({
                                        ...newTarea,
                                        usuarios_asignados: newTarea.usuarios_asignados.filter(id => id !== usuario.id)
                                      });
                                    }
                                  }}
                                  className="w-5 h-5 rounded text-green-600 focus:ring-green-500"
                                />
                                <div className="flex-1">
                                  <div className="font-semibold text-sm text-gray-800">
                                    {usuario.nombre} {usuario.apellido}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    <i className="fas fa-envelope mr-1" />
                                    {usuario.email}
                                  </div>
                                </div>
                              </label>
                            ))
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          💡 Puedes seleccionar uno o varios usuarios
                        </p>
                      </CardContent>
                    </Card>
                  </form>
                </div>

                {/* Footer con botones */}
                <div className="border-t bg-gray-50 px-4 sm:px-6 py-4 flex-shrink-0">
                  <div className="flex flex-col sm:flex-row gap-3 w-full">
                    <Button 
                      type="submit"
                      form="crear-tarea-form"
                      disabled={sending} 
                      className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-4 sm:py-6 text-base shadow-lg"
                    >
                      {sending ? (
                        <>
                          <i className="fas fa-spinner fa-spin mr-2" />
                          Creando Tarea...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paper-plane mr-2" />
                          ✅ Crear y Enviar Tarea
                        </>
                      )}
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsCreateDialogOpen(false)}
                      className="px-4 sm:px-6 py-4 sm:py-6"
                      disabled={sending}
                    >
                      <i className="fas fa-times mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Vista Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Columna Enviada */}
        <div>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-t-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-blue-800 flex items-center gap-2">
                <i className="fas fa-inbox" />
                Enviada
              </h3>
              <Badge className="bg-blue-600 text-white">
                {tareasPorEstado['Enviada'].length}
              </Badge>
            </div>
          </div>
          <div className="space-y-3">
            {tareasPorEstado['Enviada'].length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="p-6 text-center text-gray-500">
                  <i className="fas fa-check-circle text-3xl mb-2" />
                  <p className="text-sm">No hay tareas pendientes</p>
                </CardContent>
              </Card>
            ) : (
              tareasPorEstado['Enviada'].map(renderTareaCard)
            )}
          </div>
        </div>

        {/* Columna Ejecutándose */}
        <div>
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-t-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-yellow-800 flex items-center gap-2">
                <i className="fas fa-spinner fa-pulse" />
                Ejecutándose
              </h3>
              <Badge className="bg-yellow-600 text-white">
                {tareasPorEstado['Ejecutándose'].length}
              </Badge>
            </div>
          </div>
          <div className="space-y-3">
            {tareasPorEstado['Ejecutándose'].length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="p-6 text-center text-gray-500">
                  <i className="fas fa-play-circle text-3xl mb-2" />
                  <p className="text-sm">No hay tareas en progreso</p>
                </CardContent>
              </Card>
            ) : (
              tareasPorEstado['Ejecutándose'].map(renderTareaCard)
            )}
          </div>
        </div>

        {/* Columna Completa */}
        <div>
          <div className="bg-green-50 border-2 border-green-200 rounded-t-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-green-800 flex items-center gap-2">
                <i className="fas fa-check-circle" />
                Completa
              </h3>
              <Badge className="bg-green-600 text-white">
                {tareasPorEstado['Completa'].length}
              </Badge>
            </div>
          </div>
          <div className="space-y-3">
            {tareasPorEstado['Completa'].length === 0 ? (
              <Card className="bg-gray-50">
                <CardContent className="p-6 text-center text-gray-500">
                  <i className="fas fa-trophy text-3xl mb-2" />
                  <p className="text-sm">Aún no hay tareas completadas</p>
                </CardContent>
              </Card>
            ) : (
              tareasPorEstado['Completa'].map(renderTareaCard)
            )}
          </div>
        </div>
      </div>

      {/* Modal de detalles - Mejorado y Embellecido */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="!left-0 !top-0 !translate-x-0 !translate-y-0 !w-screen !max-w-none !h-screen !max-h-screen !rounded-none sm:!left-[50%] sm:!top-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:!w-[90vw] sm:!max-w-[90vw] sm:!h-auto sm:!max-h-[85vh] sm:!rounded-lg md:!w-[85vw] md:!max-w-[85vw] lg:!w-[900px] lg:!max-w-[900px] xl:!w-[1000px] xl:!max-w-[1000px] p-0 overflow-hidden">
          {selectedTarea && (
            <div className="flex flex-col w-full h-full min-w-0 overflow-hidden sm:max-h-[85vh]">
              {/* Header con gradiente */}
              <div 
                className="px-4 sm:px-6 py-4 border-b min-w-0 pr-12"
                style={{
                  background: selectedTarea.prioridad === 'alta' 
                    ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                    : selectedTarea.prioridad === 'media'
                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                    : 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                }}
              >
                <DialogTitle className="text-white text-lg sm:text-2xl font-bold mb-2 break-words">
                  {selectedTarea.titulo}
                </DialogTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-white/20 text-white border-white/40 backdrop-blur-sm">
                    <i className="fas fa-star mr-1" />
                    Prioridad: {selectedTarea.prioridad.toUpperCase()}
                  </Badge>
                  <Badge 
                    className={
                      selectedTarea.estado === 'Enviada' 
                        ? 'bg-blue-500 text-white' 
                        : selectedTarea.estado === 'Ejecutándose' 
                        ? 'bg-yellow-500 text-white' 
                        : 'bg-green-500 text-white'
                    }
                  >
                    {selectedTarea.estado === 'Enviada' && <i className="fas fa-inbox mr-1" />}
                    {selectedTarea.estado === 'Ejecutándose' && <i className="fas fa-spinner fa-pulse mr-1" />}
                    {selectedTarea.estado === 'Completa' && <i className="fas fa-check-circle mr-1" />}
                    {selectedTarea.estado}
                  </Badge>
                </div>
              </div>

              {/* Contenido scrolleable */}
              <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-6 py-4 min-w-0">
                <div className="space-y-6 w-full min-w-0 break-words">
                  {/* Información clave en cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full min-w-0">
                    {/* Fecha límite */}
                    <Card className={`border-2 ${
                      getDiasRestantes(selectedTarea.fecha_limite) < 0 
                        ? 'border-red-300 bg-red-50'
                        : getDiasRestantes(selectedTarea.fecha_limite) <= 1
                        ? 'border-orange-300 bg-orange-50'
                        : getDiasRestantes(selectedTarea.fecha_limite) <= 3
                        ? 'border-yellow-300 bg-yellow-50'
                        : 'border-blue-300 bg-blue-50'
                    }`}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            getDiasRestantes(selectedTarea.fecha_limite) < 0 
                              ? 'bg-red-500'
                              : getDiasRestantes(selectedTarea.fecha_limite) <= 1
                              ? 'bg-orange-500'
                              : getDiasRestantes(selectedTarea.fecha_limite) <= 3
                              ? 'bg-yellow-500'
                              : 'bg-blue-500'
                          }`}>
                            <i className="fas fa-clock text-white text-xl" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-600 mb-1">Fecha Límite</p>
                            <p className="font-semibold text-sm">
                              {new Date(selectedTarea.fecha_limite).toLocaleDateString('es-ES', {
                                weekday: 'short',
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                            <p className={`text-xs font-bold mt-1 ${
                              getDiasRestantes(selectedTarea.fecha_limite) < 0 
                                ? 'text-red-600'
                                : getDiasRestantes(selectedTarea.fecha_limite) <= 1
                                ? 'text-orange-600'
                                : getDiasRestantes(selectedTarea.fecha_limite) <= 3
                                ? 'text-yellow-700'
                                : 'text-blue-600'
                            }`}>
                              {getDiasRestantes(selectedTarea.fecha_limite) < 0 
                                ? `⚠️ Vencida hace ${Math.abs(getDiasRestantes(selectedTarea.fecha_limite))} días`
                                : getDiasRestantes(selectedTarea.fecha_limite) === 0
                                ? '🔥 Vence HOY'
                                : getDiasRestantes(selectedTarea.fecha_limite) === 1
                                ? '⏰ Vence MAÑANA'
                                : `📅 ${getDiasRestantes(selectedTarea.fecha_limite)} días restantes`
                              }
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Clase */}
                    <Card className="border-2 border-purple-300 bg-purple-50">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
                            <i className="fas fa-book text-white text-xl" />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs text-gray-600 mb-1">Clase</p>
                            <p className="font-semibold text-sm">
                              {clases.find(c => c.id === selectedTarea.clase_id)?.titulo || 'Sin clase'}
                            </p>
                            <p className="text-xs text-purple-600 font-medium mt-1">
                              <i className="fas fa-chalkboard-teacher mr-1" />
                              Materia del curso
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Asignados */}
                  <Card className="border-2 border-blue-200">
                    <CardHeader className="bg-blue-50 py-3">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <i className="fas fa-users text-blue-600" />
                          Asignados ({selectedTarea.usuarios_asignados?.length || 0})
                        </span>
                        {user?.rol === 'admin' && (
                          <button
                            onClick={() => setEditingAsignados(!editingAsignados)}
                            className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded transition-colors"
                            data-testid="edit-asignados-btn"
                          >
                            {editingAsignados ? 'Cerrar' : 'Editar'}
                          </button>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3">
                      {/* Usuarios asignados actuales */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        {(selectedTarea.usuarios_asignados || []).map(uid => {
                          const u = usuarios.find(usr => usr.id === uid);
                          return (
                            <span key={uid} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded-full">
                              <i className="fas fa-user text-blue-400" />
                              {u ? `${u.nombre || ''} ${u.apellido || ''}`.trim() : uid}
                            </span>
                          );
                        })}
                        {(!selectedTarea.usuarios_asignados || selectedTarea.usuarios_asignados.length === 0) && (
                          <span className="text-gray-400 text-sm">Sin asignar</span>
                        )}
                      </div>

                      {/* Panel de edición */}
                      {editingAsignados && selectedTarea.clase_id && (() => {
                        const clase = clases.find(c => c.id === selectedTarea.clase_id);
                        const estudiantesClase = clase?.estudiantes_inscritos
                          ? usuarios.filter(u => u.rol === 'estudiante' && clase.estudiantes_inscritos.includes(u.id))
                          : [];
                        return (
                          <div className="border-t pt-2 mt-2 space-y-1 max-h-48 overflow-y-auto">
                            <p className="text-xs text-gray-500 font-medium mb-1">Estudiantes de la clase:</p>
                            {estudiantesClase.map(est => {
                              const isChecked = (selectedTarea.usuarios_asignados || []).includes(est.id);
                              return (
                                <label key={est.id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer text-sm">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      const current = selectedTarea.usuarios_asignados || [];
                                      const updated = e.target.checked
                                        ? [...current, est.id]
                                        : current.filter(id => id !== est.id);
                                      handleUpdateAsignados(selectedTarea.id, updated);
                                    }}
                                    className="rounded"
                                  />
                                  {est.nombre || ''} {est.apellido || ''}
                                </label>
                              );
                            })}
                            {estudiantesClase.length === 0 && (
                              <p className="text-gray-400 text-xs">No hay estudiantes inscritos en esta clase</p>
                            )}
                          </div>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Descripción */}
                  <Card className="border-2 border-gray-200">
                    <CardHeader className="bg-gray-50 py-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <i className="fas fa-align-left text-gray-600" />
                        Descripción de la Tarea
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <p className="text-gray-700 whitespace-pre-wrap leading-relaxed break-words">
                        {selectedTarea.descripcion}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Comentarios */}
                  <Card className="border-2 border-indigo-200">
                    <CardHeader className="bg-indigo-50 py-3">
                      <CardTitle className="text-base flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <i className="fas fa-comments text-indigo-600" />
                          Comentarios y Comunicación
                        </span>
                        <Badge className="bg-indigo-600 text-white">
                          {selectedTarea.comentarios?.length || 0}
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      {/* Lista de comentarios */}
                      <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                        {selectedTarea.comentarios && selectedTarea.comentarios.length > 0 ? (
                          selectedTarea.comentarios.map((comentario, index) => (
                            <div 
                              key={comentario.id} 
                              className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                                    {comentario.usuario_nombre.charAt(0).toUpperCase()}
                                  </div>
                                  <div>
                                    <span className="font-semibold text-sm text-gray-800">
                                      {comentario.usuario_nombre}
                                    </span>
                                    <p className="text-xs text-gray-500">
                                      <i className="fas fa-clock mr-1" />
                                      {new Date(comentario.fecha).toLocaleDateString('es-ES', {
                                        day: 'numeric',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </p>
                                  </div>
                                </div>
                                <Badge variant="outline" className="text-xs">
                                  #{index + 1}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-700 ml-10 leading-relaxed break-words">
                                {comentario.texto}
                              </p>
                              
                              {/* Archivos adjuntos */}
                              {comentario.adjuntos && comentario.adjuntos.length > 0 && (
                                <div className="ml-10 mt-3 space-y-2">
                                  {comentario.adjuntos.map((adjunto) => (
                                    <button
                                      key={adjunto.id}
                                      onClick={async () => {
                                        try {
                                          const raw = adjunto.archivo_url || '';
                                          const filename = raw.replace(/^\/api\/files\//, '').replace(/^\/uploads\//, '').replace(/^\/api\/uploads\//, '');
                                          const backendBase = API.replace('/api', '');
                                          const finalUrl = raw.startsWith('http') ? raw : `${backendBase}/api/files/${filename}`;
                                          const response = await axios.get(finalUrl, { responseType: 'blob' });
                                          const blob = new Blob([response.data]);
                                          const url = window.URL.createObjectURL(blob);
                                          const link = document.createElement('a');
                                          link.href = url;
                                          link.download = adjunto.nombre_archivo;
                                          document.body.appendChild(link);
                                          link.click();
                                          document.body.removeChild(link);
                                          window.URL.revokeObjectURL(url);
                                          toast.success('Archivo descargado');
                                        } catch (error) {
                                          console.error('Error downloading file:', error);
                                          toast.error('Error al descargar archivo');
                                        }
                                      }}
                                      className="w-full flex items-center gap-2 bg-white rounded-lg p-3 border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-all group cursor-pointer"
                                    >
                                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                        {adjunto.tipo_archivo === 'pdf' && <i className="fas fa-file-pdf text-red-600 text-xl" />}
                                        {(adjunto.tipo_archivo === 'doc' || adjunto.tipo_archivo === 'docx') && <i className="fas fa-file-word text-blue-600 text-xl" />}
                                        {(adjunto.tipo_archivo === 'xls' || adjunto.tipo_archivo === 'xlsx') && <i className="fas fa-file-excel text-green-600 text-xl" />}
                                        {!['pdf', 'doc', 'docx', 'xls', 'xlsx'].includes(adjunto.tipo_archivo) && <i className="fas fa-file text-gray-600 text-xl" />}
                                      </div>
                                      <div className="flex-1 min-w-0 text-left">
                                        <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-blue-700">
                                          {adjunto.nombre_archivo}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {(adjunto.tamaño / 1024).toFixed(2)} KB • {adjunto.tipo_archivo.toUpperCase()}
                                        </p>
                                      </div>
                                      <div className="flex-shrink-0">
                                        <i className="fas fa-download text-blue-600 group-hover:text-blue-700" />
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-8">
                            <i className="fas fa-comments text-4xl text-gray-300 mb-2" />
                            <p className="text-sm text-gray-500">
                              No hay comentarios aún
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Sé el primero en comentar
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Formulario de nuevo comentario */}
                      <form onSubmit={handleAgregarComentario} className="space-y-3">
                        <Label className="text-sm font-semibold text-gray-700">
                          <i className="fas fa-pen mr-1" />
                          Agregar Comentario
                        </Label>
                        <div className="flex gap-2">
                          <Textarea
                            value={comentarioText}
                            onChange={(e) => setComentarioText(e.target.value)}
                            placeholder="Escribe tu comentario aquí... (preguntas, actualizaciones, dudas)"
                            rows={3}
                            className="flex-1 resize-none border-2 border-indigo-200 focus:border-indigo-500"
                          />
                          <Button 
                            type="submit" 
                            size="sm" 
                            className="bg-indigo-600 hover:bg-indigo-700 h-auto px-4"
                          >
                            <i className="fas fa-paper-plane text-lg" />
                          </Button>
                        </div>
                        
                        {/* Input de archivo adjunto */}
                        <div className="bg-blue-50 rounded-lg p-3 border-2 border-blue-200">
                          <Label className="text-xs font-semibold text-blue-800 mb-2 block">
                            <i className="fas fa-paperclip mr-1" />
                            Adjuntar Archivo (Opcional)
                          </Label>
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.xls,.xlsx"
                              onChange={(e) => setComentarioFile(e.target.files[0])}
                              className="text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
                              id="comentario-file-input"
                            />
                            {comentarioFile && (
                              <button
                                type="button"
                                onClick={() => setComentarioFile(null)}
                                className="text-red-600 hover:text-red-800 transition-colors"
                                title="Quitar archivo"
                              >
                                <i className="fas fa-times-circle" />
                              </button>
                            )}
                          </div>
                          {comentarioFile && (
                            <div className="mt-2 text-xs text-blue-700 bg-blue-100 rounded p-2">
                              <i className="fas fa-file mr-1" />
                              <strong>Seleccionado:</strong> {comentarioFile.name} ({(comentarioFile.size / 1024).toFixed(2)} KB)
                            </div>
                          )}
                          <p className="text-xs text-blue-600 mt-2">
                            📎 Formatos permitidos: Word (.doc, .docx), Excel (.xls, .xlsx), PDF
                          </p>
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          💡 Tip: Usa comentarios para preguntar dudas, dar actualizaciones o enviar archivos
                        </p>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Footer con botones de acción.
                  Simplificado: usamos botones explícitos en lugar de Select
                  para evitar problemas de touch en mobile (los dropdowns
                  Radix dentro de scroll contexts no responden bien al touch). */}
              <div className="border-t bg-gray-50 px-3 sm:px-6 py-3 flex-shrink-0">
                {/* Botones para estudiantes (assigned users) - quick action */}
                {selectedTarea.usuarios_asignados?.includes(user.id) && selectedTarea.estado !== 'Completa' && (
                  <div className="flex flex-col sm:flex-row gap-2 w-full mb-3">
                    {selectedTarea.estado === 'Enviada' && (
                      <Button
                        type="button"
                        className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold py-3 text-sm shadow-lg"
                        onClick={() => {
                          handleCambiarEstado(selectedTarea.id, 'Ejecutándose');
                          setIsDetailDialogOpen(false);
                        }}
                        data-testid="btn-iniciar-tarea"
                      >
                        <i className="fas fa-play mr-2" />
                        Iniciar Tarea Ahora
                      </Button>
                    )}
                    {selectedTarea.estado === 'Ejecutándose' && (
                      <Button
                        type="button"
                        className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold py-3 text-sm shadow-lg"
                        onClick={() => {
                          handleCambiarEstado(selectedTarea.id, 'Completa');
                          setIsDetailDialogOpen(false);
                        }}
                        data-testid="btn-completar-tarea"
                      >
                        <i className="fas fa-check-circle mr-2" />
                        Marcar como Completa
                      </Button>
                    )}
                  </div>
                )}

                {/* Controles de Profesor/Admin - botones explícitos (no Select) */}
                {(user.rol === 'admin' || user.rol === 'profesor') && (
                  <div className="space-y-2">
                    {/* Separador si hay botones de estudiante arriba */}
                    {selectedTarea.usuarios_asignados?.includes(user.id) && selectedTarea.estado !== 'Completa' && (
                      <div className="border-t pt-2">
                        <p className="text-xs text-gray-600 font-semibold mb-1">
                          <i className="fas fa-user-shield mr-1" />
                          Controles de {user.rol === 'admin' ? 'Administrador' : 'Profesor'}
                        </p>
                      </div>
                    )}

                    {/* Botones de estado - 3 botones en grid */}
                    <div>
                      <p className="text-xs text-gray-600 font-semibold mb-1.5">
                        <i className="fas fa-exchange-alt mr-1" />
                        Cambiar estado:
                      </p>
                      <div className="grid grid-cols-3 gap-1.5">
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedTarea.estado === 'Enviada' ? 'default' : 'outline'}
                          onClick={() => handleCambiarEstadoAdmin(selectedTarea.id, 'Enviada')}
                          disabled={selectedTarea.estado === 'Enviada'}
                          className={`text-xs px-2 py-2 h-auto ${selectedTarea.estado === 'Enviada' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-300 text-blue-700 hover:bg-blue-50'}`}
                          data-testid="btn-estado-enviada"
                        >
                          <i className="fas fa-inbox mr-1 text-[10px]" />
                          Enviada
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedTarea.estado === 'Ejecutándose' ? 'default' : 'outline'}
                          onClick={() => handleCambiarEstadoAdmin(selectedTarea.id, 'Ejecutándose')}
                          disabled={selectedTarea.estado === 'Ejecutándose'}
                          className={`text-xs px-2 py-2 h-auto ${selectedTarea.estado === 'Ejecutándose' ? 'bg-yellow-500 hover:bg-yellow-600 text-white' : 'border-yellow-300 text-yellow-700 hover:bg-yellow-50'}`}
                          data-testid="btn-estado-ejecutandose"
                        >
                          <i className="fas fa-spinner mr-1 text-[10px]" />
                          En curso
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant={selectedTarea.estado === 'Completa' ? 'default' : 'outline'}
                          onClick={() => handleCambiarEstadoAdmin(selectedTarea.id, 'Completa')}
                          disabled={selectedTarea.estado === 'Completa'}
                          className={`text-xs px-2 py-2 h-auto ${selectedTarea.estado === 'Completa' ? 'bg-green-600 hover:bg-green-700 text-white' : 'border-green-300 text-green-700 hover:bg-green-50'}`}
                          data-testid="btn-estado-completa"
                        >
                          <i className="fas fa-check-circle mr-1 text-[10px]" />
                          Completa
                        </Button>
                      </div>
                    </div>

                    {/* Botones eliminar/cerrar */}
                    <div className="flex gap-2 pt-1">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => handleEliminarTarea(selectedTarea.id)}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-xs"
                        data-testid="btn-eliminar-tarea"
                      >
                        <i className="fas fa-trash mr-1" />
                        Eliminar
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setIsDetailDialogOpen(false)}
                        className="flex-1 text-xs"
                        data-testid="btn-cerrar-modal"
                      >
                        <i className="fas fa-times mr-1" />
                        Cerrar
                      </Button>
                    </div>
                  </div>
                )}

                {/* Si es solo estudiante sin controles adicionales */}
                {!(user.rol === 'admin' || user.rol === 'profesor') && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDetailDialogOpen(false)}
                    className="w-full"
                    data-testid="btn-cerrar-modal-estudiante"
                  >
                    <i className="fas fa-times mr-2" />
                    Cerrar
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TareasKanban;
